/**
 * DECJUBA theme data layer for GA4 via Google Tag Manager (#268, #270).
 *
 * Contract: docs/08-documentation/analytics/ga4-event-map.md. The theme owns
 * the ecommerce events view_item_list, select_item, view_item, add_to_cart,
 * remove_from_cart, view_cart, view_promotion, select_promotion and
 * add_to_wishlist, and the engagement events search, sort, category_filter,
 * main_menu_click, sub_menu_click, login, account_created, enquiry_submit and
 * faq_click (all this file; find_in_store, location_search and location_cta
 * are pushed by snippets/find-in-store.liquid through push()). The Customer
 * Events pixel owns begin_checkout through purchase; page views are GTM's.
 * Nothing here is pushed by anything else.
 *
 * Every push carries `market`, `currency` and (when logged in) `customer_id`
 * from the config JSON rendered by snippets/analytics.liquid. Ecommerce pushes
 * are preceded by `{ ecommerce: null }` per Google's guidance.
 *
 * Product payloads come from `data-analytics` attributes (snippets/
 * product-analytics.liquid for Liquid cards, Searchspring.renderCard for
 * Searchspring cards) and from `/cart.js` lines for cart events. Every item
 * carries `variant_id` and `sku` for the selected variant beside `item_id` and
 * `sku_ids`: the identifiers Lexer matches on, in that order (#191). Lists are
 * any element with `data-analytics-list` (name) and `data-analytics-list-id`; a
 * card belongs to its closest list ancestor, so nested containers never
 * double-count.
 *
 * Promotion payloads come from `data-promotion` attributes (snippets/
 * promotion-analytics.liquid) on hero slides, banner tiles, tile-carousel /
 * image-carousel / image-grid / image-text-grid items and mega-menu tiles; the
 * `data-promotion-group` on their container numbers the creative_slot (#269).
 * add_to_wishlist resolves the Swym `wishlist:change` product against those
 * same card / PDP payloads.
 *
 * Gift-card products are excluded from every event here, matching the pixel:
 * a gift card is a stored-value transfer, not merchandise revenue.
 *
 * Engagement payloads are top-level keys (no `ecommerce` object). Searchspring
 * results, sort and facet changes arrive as `searchspring:results`,
 * `searchspring:sort` and `searchspring:filter` document events from
 * snippets/searchspring-results.liquid; the native search page seeds its term
 * and count into the config; nav clicks read the `data-nav-level` markers in
 * the header, mega-menu and mobile-nav Liquid; sections/form.liquid dispatches
 * `form:submitted`; FAQ items are `details.faq-item` toggles; login and
 * account_created are inferred once per session from the functions app-proxy
 * account summary.
 */
(function () {
  'use strict';
  if (window.dcAnalytics) return;

  var LIST_KEY = 'dc:analytics:list';

  function readConfig() {
    var el = document.querySelector('script[data-dc-analytics-config]');
    if (!el) return {};
    try {
      return JSON.parse(el.textContent) || {};
    } catch (e) {
      return {};
    }
  }
  var cfg = readConfig();

  window.dataLayer = window.dataLayer || [];

  function round2(n) {
    var v = Number(n);
    return isFinite(v) ? Math.round(v * 100) / 100 : 0;
  }
  function centsToUnits(cents) {
    return round2(Number(cents || 0) / 100);
  }
  function str(v) {
    return v == null ? '' : String(v);
  }
  function collapse(text) {
    return str(text).replace(/\s+/g, ' ').trim();
  }
  function guard(fn) {
    return function (event) {
      try {
        fn(event);
      } catch (e) {
        // tracking never breaks the page
      }
    };
  }

  function push(eventName, payload) {
    var out = { event: eventName, market: cfg.market || '', currency: cfg.currency || '' };
    if (cfg.customer_id) out.customer_id = String(cfg.customer_id);
    for (var key in payload) {
      if (Object.prototype.hasOwnProperty.call(payload, key) && payload[key] !== undefined) out[key] = payload[key];
    }
    if (out.ecommerce) {
      if (!out.ecommerce.currency) out.ecommerce.currency = cfg.currency || '';
      window.dataLayer.push({ ecommerce: null });
    }
    window.dataLayer.push(out);
    return out;
  }

  /* ---------- items ---------- */

  /* #343: every list scan and every wishlist:change reads every card's
     payload, so each element's parse is cached against the raw attribute
     string it came from. A changed string (Searchspring's #264 market
     re-pricing rewrites it, as could any later writer) misses the cache and
     is parsed again, so no writer has to invalidate anything. Callers only
     read the returned object; itemFromPayload copies what it needs. */
  var payloadCache = typeof WeakMap === 'function' ? new WeakMap() : null;

  function parsePayload(el) {
    if (!el) return null;
    var raw = el.getAttribute('data-analytics');
    if (!raw) return null;
    var cached = payloadCache && payloadCache.get(el);
    if (cached && cached.raw === raw) return cached.parsed;
    var parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      parsed = null;
    }
    if (payloadCache) payloadCache.set(el, { raw: raw, parsed: parsed });
    return parsed;
  }

  function itemFromPayload(p, extra) {
    extra = extra || {};
    var price = p.price == null ? undefined : round2(p.price);
    var discount = 0;
    if (p.compare_at != null && price != null && Number(p.compare_at) > price) {
      discount = round2(Number(p.compare_at) - price);
    }
    var item = {
      item_id: str(p.item_id),
      variant_id: p.variant_id != null ? str(p.variant_id) : '',
      sku: str(p.sku),
      item_name: str(p.item_name),
      price: price,
      discount: discount,
      index: extra.index != null ? extra.index : 0,
      item_brand: str(p.item_brand),
      item_category: str(p.item_category),
      item_category2: str(p.item_category2),
      item_category3: str(p.item_category3),
      item_category4: str(p.item_category4),
      item_variant: str(p.item_variant),
      item_list_name: extra.item_list_name != null ? extra.item_list_name : '',
      item_list_id: extra.item_list_id != null ? extra.item_list_id : '',
      quantity: extra.quantity != null ? extra.quantity : 1,
    };
    if (p.sku_ids) item.sku_ids = str(p.sku_ids);
    if (price === undefined) delete item.price;
    return item;
  }

  /* The PDP payload sections/main-product.liquid renders (snippets/
     product-analytics.liquid). Parsed on every call, never cached: the theme
     editor re-renders the section in place. Null when absent or malformed. */
  function readProductPayload() {
    var el = document.querySelector('script[data-analytics-product]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent) || null;
    } catch (e) {
      return null;
    }
  }

  /* The hidden `_aux_*` line properties every add path sends (#336, event map
     §11): the checkout pixel cannot see product tags, so these carry
     item_category / item_category2 / the size / the gift-card flag to it.
     `source` is a product payload, an element carrying `data-analytics`, or
     null; `size` is the chosen variant's Size value when the path knows it.
     Returns a fresh object of non-empty strings only (Shopify would otherwise
     store `""`), and never throws, so an add is never lost to analytics. */
  function lineProps(source, size) {
    var props = {};
    try {
      var p = source && typeof source.getAttribute === 'function' ? parsePayload(source) : source;
      if (p && typeof p === 'object') {
        if (str(p.item_category)) props._aux_category = str(p.item_category);
        if (str(p.item_category2)) props._aux_category2 = str(p.item_category2);
        if (p.gift_card === true) props._aux_gift_card = '1';
      }
      if (str(size)) props._aux_category3 = str(size);
    } catch (e) {
      // tracking never breaks an add
    }
    return props;
  }

  function lineProp(line, key) {
    var props = (line && line.properties) || {};
    return props[key] != null ? String(props[key]) : '';
  }

  function lineSize(line) {
    var fromProp = lineProp(line, '_aux_category3');
    if (fromProp) return fromProp;
    var options = (line && line.options_with_values) || [];
    for (var i = 0; i < options.length; i++) {
      if (options[i] && String(options[i].name || '').toLowerCase() === 'size') return str(options[i].value);
    }
    return '';
  }

  /* /cart.js line → GA4 item. `original_price` is the unit list price and
     `final_price` the unit price after line-level discounts (Insider, codes,
     automatic), so their difference is the per-unit discount. */
  function itemFromLine(line, extra) {
    extra = extra || {};
    var unitPrice = centsToUnits(line.original_price != null ? line.original_price : line.price);
    var unitFinal = centsToUnits(line.final_price != null ? line.final_price : line.price);
    var item = {
      item_id: str(line.product_id),
      variant_id: str(line.variant_id),
      sku: str(line.sku),
      item_name: line.variant_title ? line.product_title + ' - ' + line.variant_title : str(line.product_title),
      price: unitPrice,
      discount: round2(Math.max(0, unitPrice - unitFinal)),
      index: extra.index != null ? extra.index : 0,
      item_brand: str(line.vendor),
      item_category: lineProp(line, '_aux_category'),
      item_category2: lineProp(line, '_aux_category2'),
      item_category3: lineSize(line),
      item_category4: '',
      item_variant: str(line.variant_title),
      item_list_name: '',
      item_list_id: '',
      quantity: extra.quantity != null ? extra.quantity : Number(line.quantity || 1),
    };
    if (line.sku) item.sku_ids = str(line.sku);
    return item;
  }

  function isGiftCardLine(line) {
    return !!(line && (line.gift_card === true || lineProp(line, '_aux_gift_card')));
  }

  function sumValue(items) {
    return round2(
      items.reduce(function (sum, item) {
        return sum + (Number(item.price) || 0) * (Number(item.quantity) || 1);
      }, 0)
    );
  }

  /* view_cart value is the cart subtotal the customer sees: list price minus
     per-unit line discounts (Insider, automatic, codes), before shipping and
     tax. add_to_cart / remove_from_cart keep plain price × quantity. */
  function sumDiscountedValue(items) {
    return round2(
      items.reduce(function (sum, item) {
        var unit = (Number(item.price) || 0) - (Number(item.discount) || 0);
        return sum + Math.max(0, unit) * (Number(item.quantity) || 1);
      }, 0)
    );
  }

  /* ---------- lists: view_item_list + select_item ---------- */

  function visible(el) {
    return !!(el && el.getClientRects && el.getClientRects().length);
  }

  function listInfo(container) {
    return {
      name: container.getAttribute('data-analytics-list') || '',
      id: container.getAttribute('data-analytics-list-id') || '',
    };
  }

  /* Cards grouped by their closest list container. Hidden cards (the native
     grid behind a Searchspring grid, an inactive rail tab) are skipped. */
  function collectLists() {
    var groups = [];
    var byContainer = [];
    var cards = document.querySelectorAll('[data-analytics-list] [data-analytics]');
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      if (!visible(card)) continue;
      var container = card.closest('[data-analytics-list]');
      if (!container) continue;
      var idx = byContainer.indexOf(container);
      if (idx === -1) {
        byContainer.push(container);
        groups.push({ container: container, cards: [card] });
      } else {
        groups[idx].cards.push(card);
      }
    }
    return groups;
  }

  var seenLists = {};

  /* Searchspring cards on a non-AUD market render with `price: null` until the
     #264 viewport re-pricing lands (one products/<handle>.js fetch per visible
     card; cards below the fold or not published to the market never settle).
     A list with un-priced cards is therefore held until `searchspring:priced`
     reports a drained batch, or PRICE_WAIT_MS at most, then pushed with the
     prices that did land. AUD lists and Liquid cards are never held. */
  var PRICE_WAIT_MS = 2500;
  var pendingSince = {};
  var pricedAt = 0;
  var retryTimer = null;

  function scheduleRetry(delay) {
    if (retryTimer) return;
    retryTimer = setTimeout(function () {
      retryTimer = null;
      scheduleScan();
    }, Math.max(50, delay));
  }

  function shouldHold(signature, unpriced) {
    if (!unpriced) return false;
    var now = Date.now();
    var since = pendingSince[signature];
    if (!since) {
      pendingSince[signature] = now;
      scheduleRetry(PRICE_WAIT_MS);
      return true;
    }
    if (pricedAt >= since) return false;
    if (now - since >= PRICE_WAIT_MS) return false;
    scheduleRetry(PRICE_WAIT_MS - (now - since));
    return true;
  }

  /* #342: a list whose visible set changes without new cards (the wishlist
     reflows the next page's first card into page 1 when an item is removed)
     forms a new signature over cards that were already reported. Each
     reported card element is remembered, and a new signature pushes only the
     cards not yet reported, still indexed by their position among the list's
     visible cards. Re-rendered cards are new elements, so a Searchspring
     filter, sort or page change still reports its new result set (event map
     §3), and a result set seen before is still skipped on its signature. The
     unpriced hold keys on the cards being pushed. */
  var reportedCards = typeof WeakSet === 'function' ? new WeakSet() : null;

  function itemIds(items) {
    return items.map(function (it) { return it.item_id; }).join(',');
  }

  function scanLists() {
    var groups = collectLists();
    groups.forEach(function (group) {
      var info = listInfo(group.container);
      if (!info.name) return;
      var items = [];
      var fresh = [];
      var freshCards = [];
      var unpriced = 0;
      group.cards.forEach(function (card) {
        var p = parsePayload(card);
        if (!p || p.gift_card) return;
        var item = itemFromPayload(p, { index: items.length, item_list_name: info.name, item_list_id: info.id });
        items.push(item);
        if (reportedCards && reportedCards.has(card)) return;
        if (p.price == null) unpriced++;
        fresh.push(item);
        freshCards.push(card);
      });
      if (!items.length) return;
      var signature = info.name + '|' + info.id + '|' + itemIds(items);
      if (seenLists[signature]) return;
      if (!fresh.length) {
        seenLists[signature] = true;
        return;
      }
      var pushSignature = info.name + '|' + info.id + '|' + itemIds(fresh);
      if (shouldHold(pushSignature, unpriced)) return;
      seenLists[signature] = true;
      delete pendingSince[pushSignature];
      if (reportedCards) freshCards.forEach(function (card) { reportedCards.add(card); });
      push('view_item_list', {
        ecommerce: { item_list_name: info.name, item_list_id: info.id, items: fresh },
      });
    });
  }

  var scanTimer = null;
  function scheduleScan() {
    if (scanTimer) return;
    scanTimer = setTimeout(function () {
      scanTimer = null;
      try {
        scanLists();
      } catch (e) {
        // tracking never breaks the page
      }
      try {
        scanPromotions();
      } catch (e) {
        // tracking never breaks the page
      }
    }, 250);
  }

  function observeLists() {
    scheduleScan();
    if (typeof MutationObserver === 'function') {
      var observer = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          if (mutations[i].addedNodes && mutations[i].addedNodes.length) {
            scheduleScan();
            return;
          }
        }
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }
    document.addEventListener('searchspring:ready', scheduleScan);
    document.addEventListener('searchspring:priced', function () {
      pricedAt = Date.now();
      scheduleScan();
    });
  }

  function cardIndex(card, container) {
    var siblings = container.querySelectorAll('[data-analytics]');
    var index = 0;
    for (var i = 0; i < siblings.length; i++) {
      if (siblings[i] === card) return index;
      if (visible(siblings[i])) index++;
    }
    return 0;
  }

  function onCardClick(event) {
    var anchor = event.target && event.target.closest ? event.target.closest('a[href]') : null;
    if (!anchor) return;
    var card = anchor.closest('[data-analytics]');
    if (!card) return;
    var p = parsePayload(card);
    if (!p || p.gift_card) return;
    var container = card.closest('[data-analytics-list]');
    var info = container ? listInfo(container) : { name: '', id: '' };
    var index = container ? cardIndex(card, container) : 0;
    var item = itemFromPayload(p, { index: index, item_list_name: info.name, item_list_id: info.id });
    push('select_item', {
      ecommerce: { item_list_name: info.name, item_list_id: info.id, items: [item] },
    });
    try {
      sessionStorage.setItem(LIST_KEY, JSON.stringify({ name: info.name, id: info.id, item_id: item.item_id }));
    } catch (e) {
      // storage unavailable
    }
  }

  /* ---------- view_item ---------- */

  function trackProductView() {
    var p = readProductPayload();
    if (!p || p.gift_card) return;
    var list = { name: '', id: '' };
    try {
      var stored = JSON.parse(sessionStorage.getItem(LIST_KEY) || 'null');
      if (stored && String(stored.item_id) === String(p.item_id)) list = { name: stored.name || '', id: stored.id || '' };
      sessionStorage.removeItem(LIST_KEY);
    } catch (e) {
      // storage unavailable
    }
    var item = itemFromPayload(p, { index: 0, item_list_name: list.name, item_list_id: list.id });
        var stockStatus = p.stock_status != null ? Number(p.stock_status) : undefined;
    push('view_item', {
      stock_status: stockStatus,
      stockStatus: stockStatus,
      ecommerce: { value: item.price, items: [item] },
    });
  }

  /* ---------- cart ---------- */

  function fetchCart() {
    return fetch(cfg.cart_url || '/cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then(
      function (response) {
        return response.ok ? response.json() : Promise.reject(response);
      }
    );
  }

  function trackCartDelta(eventName, item) {
    push(eventName, { ecommerce: { value: sumValue([item]), items: [item] } });
  }

  /* Resolve the just-added line from /cart.js and report it once. Shared by
     cart:added (product form, quick-add panel/sheet, quick-add modal,
     quick-shop) and the upsell band's cart:updated `detail.added`. Neither
     path trusts the add response itself: routes.cart_add_url without `.js`
     answers JSON with string dollar prices, a null final_price and no
     options, while /cart.js is the canonical cents shape and already carries
     the Function-discounted final_price the customer sees. Automatic gift
     lines dispatch neither signal, so they are never reported here. */
  function trackAddedVariant(variantId, quantity) {
    variantId = Number(variantId);
    if (!variantId) return;
    quantity = Number(quantity) || 1;
    fetchCart()
      .then(function (cart) {
        var items = (cart && cart.items) || [];
        for (var i = 0; i < items.length; i++) {
          if (Number(items[i].variant_id) === variantId) {
            if (isGiftCardLine(items[i])) return;
            trackCartDelta('add_to_cart', itemFromLine(items[i], { quantity: quantity }));
            return;
          }
        }
      })
      .catch(function () {
        // a cart read failure costs one event, never the add
      });
  }

  function onCartAdded(event) {
    var detail = (event && event.detail) || {};
    trackAddedVariant(detail.variantId, detail.quantity);
  }

  /* cart:updated is a generic "cart re-rendered" signal (bag-page edits,
     gift automation, customer sync, the upsell band). Only the upsell band's
     dispatch carries `detail.added` ({ variantId, quantity }, decision Q5:
     upsell adds are customer actions), so that is the only cart:updated that
     produces an add_to_cart, for exactly the line just added. Never rescan
     `detail.cart.items` here: `_upsell_item` persists on the line, so a
     rescan would re-report the same add on every later, unrelated bag edit. */
  function onCartUpdated(event) {
    var detail = (event && event.detail) || {};
    var added = detail.added;
    if (!added) return;
    trackAddedVariant(added.variantId, added.quantity);
  }

  /* Bag page quantity changes and removals (sections/cart.liquid snapshot).
     Quantity up is add_to_cart for the delta, quantity down or remove is
     remove_from_cart for the delta. The snapshot carries the row's unit list
     price (`originalPrice`) and discounted unit price (`price`), so the item
     reports the same price/discount split as itemFromLine. */
  function onLineChanged(event) {
    var d = (event && event.detail) || {};
    if (d.giftCard) return;
    var previous = Number(d.previousQuantity) || 0;
    var next = d.type === 'remove' ? 0 : Number(d.quantity) || 0;
    var delta = next - previous;
    if (!delta) return;
    var unitFinal = centsToUnits(d.price);
    var unitPrice = d.originalPrice != null ? centsToUnits(d.originalPrice) : unitFinal;
    var item = {
      item_id: str(d.productId),
      variant_id: str(d.variantId),
      sku: str(d.sku),
      item_name: d.variantTitle ? str(d.title) + ' - ' + d.variantTitle : str(d.title),
      price: unitPrice,
      discount: round2(Math.max(0, unitPrice - unitFinal)),
      index: 0,
      item_brand: str(d.vendor),
      item_category: str(d.category),
      item_category2: str(d.category2),
      item_category3: '',
      item_category4: '',
      item_variant: str(d.variantTitle),
      item_list_name: '',
      item_list_id: '',
      quantity: Math.abs(delta),
    };
    if (d.sku) item.sku_ids = str(d.sku);
    trackCartDelta(delta > 0 ? 'add_to_cart' : 'remove_from_cart', item);
  }

  /* Every cart line is reported, gift-with-purchase lines included; only
     gift-card PRODUCTS are dropped, the same rule as every other event here
     and as the checkout pixel, so browse and purchase revenue agree. */
  function trackCartView() {
    fetchCart()
      .then(function (cart) {
        var lines = ((cart && cart.items) || []).filter(function (line) {
          return !isGiftCardLine(line);
        });
        if (!lines.length) return;
        var items = lines.map(function (line, index) {
          return itemFromLine(line, { index: index });
        });
        push('view_cart', { ecommerce: { value: sumDiscountedValue(items), items: items } });
      })
      .catch(function () {
        // never breaks the cart
      });
  }

  function onOverlayOpen(event) {
    var target = event && event.target;
    if (target && target.closest && target.closest('cart-drawer')) trackCartView();
  }

  /* ---------- promotions: view_promotion + select_promotion ---------- */

  /* A promotion is any element carrying `data-promotion` (snippets/
     promotion-analytics.liquid: { promotion_id, promotion_name, creative_name }).
     Its creative_slot is its 1-based DOM position among the promotions in the
     closest `[data-promotion-group]` (the section's item container), so a
     hero's second slide reports 2 wherever the section sits on the page. */
  function parsePromotion(el) {
    if (!el) return null;
    var raw = el.getAttribute('data-promotion');
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  function promotionSlot(el) {
    var group = el.closest ? el.closest('[data-promotion-group]') : null;
    if (!group) return 1;
    var siblings = group.querySelectorAll('[data-promotion]');
    for (var i = 0; i < siblings.length; i++) {
      if (siblings[i] === el) return i + 1;
    }
    return 1;
  }

  function promotionItem(el) {
    var p = parsePromotion(el);
    if (!p) return null;
    return {
      promotion_id: str(p.promotion_id),
      promotion_name: str(p.promotion_name),
      creative_name: str(p.creative_name),
      creative_slot: promotionSlot(el),
    };
  }

  /* IntersectionObserver is geometric only: a mega-menu tile in a closed panel
     (`visibility: hidden`, but laid out just under the header) intersects the
     viewport at load. Such entries are parked, not counted, and re-observed
     once the panel can have opened (a transition ended, focus or the pointer
     moved), which makes the observer re-deliver the entry against the current
     style. Slides scrolled out of a carousel track are clipped by the track,
     so they only intersect once the slider brings them into view. */
  var promotionObserver = null;
  var seenPromotions = null;
  var hiddenPromotions = [];
  var hiddenRecheckTimer = null;

  function promotionRendered(el) {
    return window.getComputedStyle(el).visibility !== 'hidden';
  }

  function onPromotionIntersect(entries) {
    for (var i = 0; i < entries.length; i++) {
      var entry = entries[i];
      var el = entry.target;
      if (seenPromotions.has(el)) continue;
      if (!entry.isIntersecting || entry.intersectionRatio < 0.5) continue;
      if (!promotionRendered(el)) {
        if (hiddenPromotions.indexOf(el) === -1) hiddenPromotions.push(el);
        continue;
      }
      seenPromotions.add(el);
      promotionObserver.unobserve(el);
      var item = promotionItem(el);
      if (item) push('view_promotion', { ecommerce: { items: [item] } });
    }
  }

  /* Only promotions that have since become visible are re-observed (which
     makes the observer re-deliver their entry); still-hidden ones stay parked,
     so a closed menu costs nothing while the pointer moves around the page. */
  function recheckHiddenPromotions() {
    if (hiddenRecheckTimer || !hiddenPromotions.length) return;
    hiddenRecheckTimer = setTimeout(function () {
      hiddenRecheckTimer = null;
      var pending = hiddenPromotions;
      hiddenPromotions = [];
      for (var i = 0; i < pending.length; i++) {
        var el = pending[i];
        if (seenPromotions.has(el) || !el.isConnected) continue;
        if (!promotionRendered(el)) {
          hiddenPromotions.push(el);
          continue;
        }
        promotionObserver.unobserve(el);
        promotionObserver.observe(el);
      }
    }, 250);
  }

  /* Runs from the same debounced scan as the lists (body MutationObserver,
     searchspring:ready), so promotions rendered after load are picked up.
     observe() on an already-observed element is a no-op. */
  function scanPromotions() {
    if (!promotionObserver) return;
    var els = document.querySelectorAll('[data-promotion]');
    for (var i = 0; i < els.length; i++) {
      if (!seenPromotions.has(els[i])) promotionObserver.observe(els[i]);
    }
  }

  function observePromotions() {
    if (!('IntersectionObserver' in window) || typeof WeakSet !== 'function') return;
    seenPromotions = new WeakSet();
    promotionObserver = new IntersectionObserver(
      function (entries) {
        try {
          onPromotionIntersect(entries);
        } catch (e) {
          // tracking never breaks the page
        }
      },
      { threshold: 0.5 }
    );
    document.addEventListener('transitionend', recheckHiddenPromotions, true);
    document.addEventListener('focusin', recheckHiddenPromotions, true);
    document.addEventListener('mouseover', recheckHiddenPromotions, true);
  }

  /* Delegated, capture phase like onCardClick. A link inside a product card is
     the card's select_item, never a select_promotion as well. Any link inside
     the promotion counts (an image-text-grid cell has a media link and up to
     two CTAs); one push per click, the navigation is never blocked. */
  function onPromotionClick(event) {
    var anchor = event.target && event.target.closest ? event.target.closest('a[href]') : null;
    if (!anchor || anchor.closest('[data-analytics]')) return;
    var promo = anchor.closest('[data-promotion]');
    if (!promo) return;
    var item = promotionItem(promo);
    if (item) push('select_promotion', { ecommerce: { items: [item] } });
  }

  /* ---------- add_to_wishlist ---------- */

  /* wishlist:change is broadcast by the Swym heart (cards, quick-shop, PDP),
     the wishlist modal and the bag's move-to-wishlist with
     { productId, variantId, added }. The product payload is the matching
     card's `data-analytics` (hearts live inside cards), else the PDP's
     `data-analytics-product` script; a product with neither on the page (a
     bag line moved to the wishlist) is not reported. Not deduped: a re-add
     after a Swym state hiccup is a genuine add. */
  function findProductPayload(productId) {
    var id = str(productId);
    if (!id) return null;
    var cards = document.querySelectorAll('[data-analytics]');
    for (var i = 0; i < cards.length; i++) {
      var p = parsePayload(cards[i]);
      if (p && str(p.item_id) === id) return p;
    }
    var pdp = readProductPayload();
    return pdp && str(pdp.item_id) === id ? pdp : null;
  }

  function onWishlistChange(event) {
    var detail = (event && event.detail) || {};
    if (detail.added !== true) return;
    var p = findProductPayload(detail.productId);
    if (!p || p.gift_card) return;
    var item = itemFromPayload(p, { index: 0 });
    var ecommerce = { value: item.price, items: [item] };
    if (item.price === undefined) delete ecommerce.value;
    push('add_to_wishlist', { ecommerce: ecommerce });
  }

  /* ---------- search ---------- */

  /* Searchspring re-renders on every refinement (facet, sort, page, typed
     query), so `search` is keyed on the term: one push per distinct query,
     none for a facet or page change under the same term. The native Liquid
     grid (config search_term / search_results) only counts when Searchspring
     is not driving the page: its grid is hidden then and the controller
     reports the same query. */
  var lastSearchTerm = null;

  function trackSearch(term, total) {
    term = collapse(term);
    if (!term || term === lastSearchTerm) return;
    lastSearchTerm = term;
    push('search', { search_term: term, search_results: Number(total) || 0, search_type: 'results' });
  }

  function onSearchspringResults(event) {
    var d = (event && event.detail) || {};
    if (d.mode !== 'search') return;
    trackSearch(d.query, d.total);
  }

  function trackNativeSearch() {
    if (cfg.template !== 'search' || window.__searchspring || cfg.search_term == null) return;
    trackSearch(cfg.search_term, cfg.search_results);
  }

  /* ---------- sort + category_filter ---------- */

  /* The theme's sort token is `field:direction`; GA4 gets `field direction`
     (live shape, e.g. `ga_item_revenue desc`). */
  function onSearchspringSort(event) {
    var d = (event && event.detail) || {};
    var value = str(d.value);
    var at = value.indexOf(':');
    push('sort', {
      sort_by: collapse(d.label),
      sort_value: at === -1 ? value : value.slice(0, at) + ' ' + value.slice(at + 1),
    });
  }

  /* One push per facet toggle and per Clear all (action `clear`, empty
     filters); the drawer's See results only closes the drawer. `filters` is
     the full active set after the change, labelled as the shopper saw it. */
  function onSearchspringFilter(event) {
    var d = (event && event.detail) || {};
    var filters = Array.isArray(d.filters) ? d.filters : [];
    push('category_filter', {
      filter_type: str(d.type),
      filter_value: str(d.value),
      filter_action: str(d.action),
      filters: filters.map(function (f) {
        return { key: str(f && f.key), label: str(f && f.label), value: str(f && f.value) };
      }),
    });
  }

  /* ---------- main_menu_click + sub_menu_click ---------- */

  /* Nav links carry `data-nav-level` from snippets/mega-menu.liquid,
     mobile-nav.liquid and nav-link.liquid: 1 = header link / mega trigger /
     mobile top level, 2 = mega column heading, lead link or tile and mobile
     child group or link, 3 = mega column link / mobile child link. A mobile
     <summary> counts on the click that opens it only (details.open is still
     false at click time), so closing a group is not a menu click. Capture
     phase and never preventDefault: navigation and the disclosure both
     proceed. A mega tile also carries data-promotion and fires
     select_promotion as well; intended, the two answer different questions. */
  function navText(el) {
    if (!el) return '';
    var label = el.querySelector('.mobile-nav__label, .mega__tile-caption');
    var text = collapse(label ? label.textContent : el.textContent);
    if (text) return text;
    var img = el.querySelector('img[alt]');
    return collapse(img ? img.getAttribute('alt') : el.getAttribute('aria-label'));
  }

  /* Outermost mobile-nav <details>: the level-1 group the click sits in. */
  function mobileTopDetails(el) {
    var details = el.closest('details.mobile-nav__details');
    while (details) {
      var outer = details.parentElement && details.parentElement.closest('details.mobile-nav__details');
      if (!outer) return details;
      details = outer;
    }
    return null;
  }

  function summaryLabel(details) {
    return details ? navText(details.querySelector('summary .mobile-nav__label')) : '';
  }

  function navMenuText(el) {
    var mega = el.closest('mega-menu');
    if (mega) return navText(mega.querySelector('[data-mega-trigger]'));
    var top = mobileTopDetails(el);
    if (top) return summaryLabel(top);
    return navText(el);
  }

  function navCategoryText(el) {
    var column = el.closest('.mega__column');
    if (column) return navText(column.querySelector('.mega__heading'));
    var child = el.closest('details.mobile-nav__details--child');
    if (child) return summaryLabel(child);
    return '';
  }

  function onNavClick(event) {
    var el = event.target && event.target.closest ? event.target.closest('[data-nav-level]') : null;
    if (!el) return;
    if (el.tagName === 'SUMMARY' && el.parentElement && el.parentElement.open) return;
    var level = Number(el.getAttribute('data-nav-level'));
    if (level === 1) {
      push('main_menu_click', { menu_text: navText(el) });
      return;
    }
    push('submenu_click', {
      menu_text: navMenuText(el),
      submenu_text: navText(el),
      category_text: level >= 3 ? navCategoryText(el) : '',
    });
  }

  /* ---------- login + account_created ---------- */

  /* Hosted new customer accounts hide the login and sign-up submits from the
     theme, so both are inferred once per browser session on the first page
     that renders with a customer (event map Q3). The functions app proxy
     (config account_summary_url) reports the customer's createdAt; within
     ACCOUNT_CREATED_WINDOW_MS of now it is a sign-up (account_created), else
     a login. Any failure of that call falls back to login, so exactly one of
     the two is pushed. The session flag is set before the fetch so a fast
     second navigation cannot double up, and the fetch is keepalive so that
     navigation cannot abort it and lose the session's one push; with no
     sessionStorage there is no once-per-session guarantee, so nothing is
     pushed. insider_status is `insider` on both events for every logged-in
     customer: Insider pricing in this theme is gated on being logged in, not
     on a tag. When the account-summary response carries `emailSha256` (SHA-256
     of the trimmed, lowercased email, hashed in the functions Worker and never
     in page HTML, #341) both events also carry `user_data.sha256_email_address`,
     the key shape the checkout pixel already sends, so Lexer can join the
     person across the historical store datasets (#191). The fallback login
     (failed or skipped fetch) goes without it. No other push carries it. */
  var LOGIN_KEY = 'dc:login-tracked';
  var ACCOUNT_CREATED_WINDOW_MS = 10 * 60 * 1000;

  function trackLogin() {
    if (!cfg.customer_id) return;
    try {
      if (sessionStorage.getItem(LOGIN_KEY)) return;
      sessionStorage.setItem(LOGIN_KEY, '1');
    } catch (e) {
      return;
    }
    var loginPayload = function (summary) {
      var payload = { method: 'shopify', insider_status: 'insider' };
      var hash = summary && summary.emailSha256;
      if (typeof hash === 'string' && hash) {
        payload.user_data = { sha256_email_address: hash };
      }
      return payload;
    };
    var pushLogin = function (summary) {
      push('login', loginPayload(summary));
    };
    if (!cfg.account_summary_url || typeof fetch !== 'function') {
      pushLogin();
      return;
    }
    fetch(cfg.account_summary_url, { credentials: 'same-origin', keepalive: true, headers: { Accept: 'application/json' } })
      .then(function (response) {
        return response.ok ? response.json() : Promise.reject(response);
      })
      .then(function (summary) {
        var created = summary && summary.createdAt ? Date.parse(summary.createdAt) : NaN;
        if (isFinite(created) && Math.abs(Date.now() - created) <= ACCOUNT_CREATED_WINDOW_MS) {
          push('account_created', loginPayload(summary));
        } else {
          pushLogin(summary);
        }
      })
      .catch(function () {
        pushLogin();
      });
  }

  /* ---------- enquiry_submit + faq_click ---------- */

  /* sections/form.liquid dispatches a bubbling form:submitted from its
     success branch with the section title handleized as detail.handle. */
  function onFormSubmitted(event) {
    var d = (event && event.detail) || {};
    push('enquiry_submit', { enquiry_type: str(d.handle) });
  }

  /* `toggle` does not bubble; a capture listener on document still sees every
     <details> toggle. Only opening counts, and only FAQ items (the FAQ block
     and product FAQs share snippets/faq-accordion-item.liquid). */
  function onDetailsToggle(event) {
    var el = event && event.target;
    if (!el || !el.matches || !el.matches('details.faq-item') || !el.open) return;
    var question = el.querySelector('.faq-item__question');
    push('faq_click', { click_text: question ? collapse(question.textContent) : '' });
  }

  /* ---------- boot ---------- */

  function boot() {
    observePromotions();
    observeLists();
    document.addEventListener('click', onCardClick, true);
    document.addEventListener('click', onPromotionClick, true);
    document.addEventListener('click', guard(onNavClick), true);
    document.addEventListener('toggle', guard(onDetailsToggle), true);
    trackProductView();
    document.addEventListener('cart:added', onCartAdded);
    document.addEventListener('cart:updated', onCartUpdated);
    document.addEventListener('cart:line-changed', onLineChanged);
    document.addEventListener('overlay:open', onOverlayOpen);
    document.addEventListener('wishlist:change', onWishlistChange);
    document.addEventListener('searchspring:results', guard(onSearchspringResults));
    document.addEventListener('searchspring:sort', guard(onSearchspringSort));
    document.addEventListener('searchspring:filter', guard(onSearchspringFilter));
    document.addEventListener('form:submitted', guard(onFormSubmitted));
    if (cfg.template === 'cart') trackCartView();
    guard(trackNativeSearch)();
    guard(trackLogin)();
  }

  window.dcAnalytics = {
    config: cfg,
    push: push,
    item: itemFromPayload,
    itemFromLine: itemFromLine,
    promotionItem: promotionItem,
    scanLists: scheduleScan,
    lineProps: lineProps,
    productPayload: readProductPayload,
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
